import { Controller } from "@hotwired/stimulus"

// Stimulus controller for progressive disclosure of form fields
// Issue #1601 - Progressive disclosure for employment & referrer fields
//
// Reveals form field groups sequentially as the user fills in preceding fields.
// Supports text inputs (reveal on non-empty) and radio buttons (reveal on specific value).
//
// Usage in view:
//   <div data-controller="progressive-reveal">
//     <!-- Always-visible trigger field -->
//     <input data-action="input->progressive-reveal#reveal"
//            data-progressive-reveal-index-param="0">
//
//     <!-- First reveal group (hidden until trigger has content) -->
//     <div data-progressive-reveal-target="revealGroup" class="d-none">
//       <input data-action="input->progressive-reveal#reveal"
//              data-progressive-reveal-index-param="1">
//     </div>
//
//     <!-- Second reveal group -->
//     <div data-progressive-reveal-target="revealGroup" class="d-none">
//       ...
//     </div>
//   </div>
//
export default class extends Controller {
  static targets = ["revealGroup"]

  connect() {
    // On connect, ensure all reveal groups start hidden
    this.revealGroupTargets.forEach((group) => {
      group.classList.add("d-none")
      group.style.overflow = "hidden"
      group.style.opacity = "0"
      group.style.maxHeight = "0"
      group.style.transition = "opacity 0.3s ease, max-height 0.4s ease"
    })

    // Check if any fields already have values (e.g., on re-render after validation)
    this._restoreState()
  }

  // Called by trigger fields via data-action="input->progressive-reveal#reveal"
  // or data-action="change->progressive-reveal#reveal"
  // The index param indicates which revealGroup to show
  reveal(event) {
    const index = event.params.index
    if (index === undefined || index === null) return

    const target = this.revealGroupTargets[index]
    if (!target) return

    const shouldShow = this._shouldReveal(event.target)

    if (shouldShow) {
      this._showGroup(target)
    } else {
      this._hideGroup(target)
      // Also hide all subsequent groups
      this._hideSubsequentGroups(index)
    }
  }

  // Determine if the trigger element has enough content to reveal the next group
  _shouldReveal(element) {
    if (element.type === "radio") {
      // For radio buttons, reveal if checked and value is truthy/specific
      // "yes", "sub" trigger reveal; "no", "prime" do not
      const revealValues = ["yes", "sub"]
      return element.checked && revealValues.includes(element.value.toLowerCase())
    }

    // For text/email/tel inputs, reveal if non-empty
    return element.value.trim().length > 0
  }

  // Show a reveal group with CSS transition
  _showGroup(group) {
    group.classList.remove("d-none")
    // Force reflow for transition
    group.offsetHeight // eslint-disable-line no-unused-expressions
    group.style.opacity = "1"
    group.style.maxHeight = "500px"
  }

  // Hide a reveal group with CSS transition
  _hideGroup(group) {
    group.style.opacity = "0"
    group.style.maxHeight = "0"
    // After transition completes, add d-none
    setTimeout(() => {
      if (group.style.opacity === "0") {
        group.classList.add("d-none")
      }
    }, 400)
  }

  // Hide all groups after the given index
  _hideSubsequentGroups(fromIndex) {
    for (let i = fromIndex + 1; i < this.revealGroupTargets.length; i++) {
      this._hideGroup(this.revealGroupTargets[i])
    }
  }

  // On page load, restore visibility for groups whose triggers already have values
  // This handles cases like form re-render after validation failure
  _restoreState() {
    // Find all elements with reveal action params within this controller scope
    const triggers = this.element.querySelectorAll("[data-progressive-reveal-index-param]")

    triggers.forEach((trigger) => {
      const index = parseInt(trigger.dataset.progressiveRevealIndexParam, 10)
      const target = this.revealGroupTargets[index]
      if (!target) return

      if (this._shouldReveal(trigger)) {
        // Immediately show without transition for pre-filled fields
        target.classList.remove("d-none")
        target.style.opacity = "1"
        target.style.maxHeight = "500px"
      }
    })
  }
}
